import { HocuspocusProvider } from '@hocuspocus/provider';
import { IndexeddbPersistence } from 'y-indexeddb';
import * as Y from 'yjs';

import type { ConnectionStatus } from '../types';
import { isMilkdownContextNotFoundError, REMOTE_SYNC_FALLBACK_MS } from './constants';
import type { SyncRefs } from './types';
import { safeDestroy } from './utils';

type CreateCollabResourcesOptions = {
  /** When false, skip IndexedDB so a failed pre-connect clear cannot remount over stale cache. */
  useIndexedDB?: boolean;
};

/** Create collab resources (ydoc, persistence, provider) */
export const createCollabResources = (room: string, url: string, token?: string, options?: CreateCollabResourcesOptions) => {
  // gc: true enables garbage collection to remove tombstones and compact history
  // This keeps Y.Doc state small since document revision history is stored in the database
  const ydoc = new Y.Doc({ gc: true });
  const useIndexedDB = options?.useIndexedDB !== false;
  const persistence = useIndexedDB ? new IndexeddbPersistence(room, ydoc) : null;

  // y-indexeddb's _storeUpdate may return a Promise (or call async work that does).
  // InvalidStateError from a closing DB occurs in async callbacks, so sync try/catch
  // misses it. If the listener returns a thenable, attach .catch() so rejections are handled.
  if (persistence) {
    const p = persistence as any;
    const origStoreUpdate = p._storeUpdate;
    if (typeof origStoreUpdate === 'function') {
      const handleError = (e: unknown) => {
        if (e instanceof DOMException && e.name === 'InvalidStateError') {
          return;
        }
        throw e;
      };
      const guarded = (...args: unknown[]) => {
        try {
          origStoreUpdate(...args)?.catch(handleError);
        } catch (e) {
          handleError(e);
        }
      };
      ydoc.off('update', origStoreUpdate);
      ydoc.on('update', guarded);
      p._storeUpdate = guarded;
    }
  }

  const provider = new HocuspocusProvider({
    url,
    name: room,
    document: ydoc,
    token,
  });

  return { ydoc, persistence, provider };
};

/** Destroy collab resources with error handling. */
export const destroyCollabResources = (provider: HocuspocusProvider, _persistence: IndexeddbPersistence | null, ydoc: Y.Doc) => {
  safeDestroy(
    () => provider.destroy(),
    (e) => {
      if (!isMilkdownContextNotFoundError(e)) {
        console.warn('[BrainfishEditor] Provider cleanup error:', e);
      }
    },
  );
  // IndexeddbPersistence registers doc.on('destroy', this.destroy), so destroying ydoc
  // triggers persistence.destroy() once. Do not call persistence.destroy() explicitly
  // or we double-destroy (_dbref underflow, IndexedDB closed twice).
  safeDestroy(() => ydoc.destroy());
};

/**
 * Wire up provider and persistence event listeners.
 * `onRemoteReady` fires when remote is considered ready (`synced`, or connected fallback)
 * so editor mount and `onSynced` share the same readiness path.
 */
export const wireProviderEvents = (
  provider: HocuspocusProvider,
  persistence: IndexeddbPersistence | null,
  refs: SyncRefs,
  onRemoteReady?: () => void,
) => {
  const checkSynced = () => {
    if (refs.local.current && refs.remote.current && !refs.called.current) {
      refs.called.current = true;
      // Wrappers read refs at call time so they work even if editor becomes ready after sync
      const replaceContent = (markdown: string) => {
        if (refs.replaceContent.current) {
          refs.replaceContent.current(markdown);
        } else {
          console.warn('[BrainfishEditor] replaceContent called before editor ready');
        }
      };
      const refreshHeadings = () => {
        if (refs.refreshHeadings.current) {
          refs.refreshHeadings.current();
        } else {
          console.warn('[BrainfishEditor] refreshHeadings called before editor ready');
        }
      };
      refs.onSynced.current?.({ replaceContent, refreshHeadings });
    }
  };

  const markRemoteReady = () => {
    refs.remote.current = true;
    onRemoteReady?.();
    checkSynced();
  };

  if (persistence) {
    persistence.on('synced', () => {
      refs.local.current = true;
      checkSynced();
    });
  } else {
    // No IndexedDB: local cache is empty by definition.
    refs.local.current = true;
  }

  let syncedFired = false;
  let syncTimeout: ReturnType<typeof setTimeout> | null = null;

  provider.on('status', ({ status }: { status: ConnectionStatus }) => {
    refs.onConnection.current?.(status);

    // Fallback: consider synced after REMOTE_SYNC_FALLBACK_MS if 'synced' never fires
    if (status === 'connected' && !syncedFired) {
      syncTimeout = setTimeout(() => {
        if (!syncedFired) {
          markRemoteReady();
        }
      }, REMOTE_SYNC_FALLBACK_MS);
    }
  });

  provider.on('synced', () => {
    syncedFired = true;
    if (syncTimeout) clearTimeout(syncTimeout);
    syncTimeout = null;
    markRemoteReady();
  });

  provider.on('authenticationFailed', ({ reason }: { reason: string }) => {
    console.error('[BrainfishEditor] Authentication failed', reason);
    refs.onAuthenticationFailed.current?.({ reason, hasUnsyncedChanges: provider.hasUnsyncedChanges });
  });

  provider.awareness?.on('change', () => {
    refs.onAwareness.current?.(provider.awareness!.getStates() as Map<number, Record<string, unknown>>);
  });

  return () => {
    if (syncTimeout) clearTimeout(syncTimeout);
  };
};
